import React, { useState } from 'react';
import { X, Download, Share2 } from 'lucide-react';

/**
 * DocumentPreviewModal - Preview images and PDFs in-app
 *
 * Displays images directly, PDFs in iframe
 * Includes download and share buttons
 */
const DocumentPreviewModal = ({ document, onClose, onDownload }) => {
  const [sharing, setSharing] = useState(false);

  if (!document) return null;

  const isImage = document.fileType.startsWith('image/');
  const isPDF = document.fileType === 'application/pdf';

  // Handle share functionality
  const handleShare = async () => {
    setSharing(true);

    try {
      // Convert base64 to blob for sharing
      const base64Data = document.fileData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: document.fileType });
      const file = new File([blob], document.fileName, { type: document.fileType });

      // Check if Web Share API is available
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        // Use native share (works great on mobile!)
        await navigator.share({
          title: document.fileName,
          text: `Document: ${document.fileName}`,
          files: [file]
        });
      } else {
        // Fallback: create mailto link for email
        const subject = encodeURIComponent(`Document: ${document.fileName}`);
        const body = encodeURIComponent(
          `Please find attached document: ${document.fileName}\n\n` +
          `Uploaded: ${new Date(document.uploadedAt).toLocaleDateString('en-GB')}\n\n` +
          `Note: This is a system-generated email. Download the file from the equipment register to attach it to your email.`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;

        // Also trigger download as a fallback
        onDownload(document);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        // User didn't cancel, show error

        // Fallback to download
        onDownload(document);
      }
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-75"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">

          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {document.fileName}
              </h3>
              <p className="text-sm text-gray-500">
                {new Date(document.uploadedAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            <div className="flex items-center gap-2 ml-4">
              {/* Share button */}
              <button
                onClick={handleShare}
                disabled={sharing}
                className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                title="Share"
              >
                <Share2 className="h-5 w-5" />
              </button>

              {/* Download button */}
              <button
                onClick={() => onDownload(document)}
                className="p-2 text-gray-600 hover:text-cyan-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Download"
              >
                <Download className="h-5 w-5" />
              </button>

              {/* Close button */}
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Preview Area */}
          <div className="flex-1 overflow-auto p-4 bg-gray-50">
            {isImage && (
              <div className="flex items-center justify-center min-h-[400px]">
                <img
                  src={document.fileData}
                  alt={document.fileName}
                  className="max-w-full h-auto rounded-lg shadow-lg"
                  style={{ maxHeight: 'calc(90vh - 200px)' }}
                />
              </div>
            )}

            {isPDF && (
              <iframe
                src={document.fileData}
                title={document.fileName}
                className="w-full rounded-lg shadow-lg"
                style={{ height: 'calc(90vh - 200px)' }}
              />
            )}

            {!isImage && !isPDF && (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
                <Download className="h-16 w-16 mb-4" />
                <p className="text-lg font-medium">Preview not available</p>
                <p className="text-sm mt-2">Click the download button to view this file</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
